import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { spatialApi } from '../lib/api';
import { run, isAbortError } from '../lib/worker-pool';
import { dbRows, mergeNearest, nearestGeoJSON } from '../lib/nearest';

export const NEAREST_DEFAULT_RADIUS_M = 1000;
export const NEAREST_DEFAULT_LIMIT = 10;

/**
 * "Find nearest" map tool (docs/TODO.md 3.10). A click calls GET /api/spatial/nearby (params
 * lat, lon, k; org-scoped `nearby_features` RPC, saved features only) and, in parallel, searches
 * the visible client-side layers (unsaved imports / results) in the worker pool (task `nearest`).
 * The radius is applied client-side because the RPC is k-nearest only.
 *
 *   search(evt | { lngLat })  run for a map click          setRadiusM(m)  re-runs at the same point
 *   clear()                   drop results                  geojson        highlight layer data
 */
export function useNearestTool({ tool, importedLayers = [], resultLayers = [] }) {
  const [point, setPoint] = useState(null);
  const [radiusM, setRadiusM] = useState(NEAREST_DEFAULT_RADIUS_M);
  const [limit, setLimit] = useState(NEAREST_DEFAULT_LIMIT);
  const [state, setState] = useState({ db: [], local: [], busy: false, error: null });
  const seq = useRef(0);
  const mounted = useRef(true);
  const abortRef = useRef(null);
  const layersRef = useRef([]);
  layersRef.current = [...importedLayers, ...resultLayers]
    .filter((l) => l.visible !== false && l.geojson?.features?.length)
    .map((l) => ({ id: l.id, name: l.name, geojson: l.geojson }));

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const execute = useCallback(async (pt, radius, k) => {
    const mine = ++seq.current;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setState((s) => ({ ...s, busy: true, error: null }));
    const [dbRes, localRes] = await Promise.allSettled([
      spatialApi.nearby(pt[1], pt[0], k).then((r) => dbRows(r.data?.results)),
      layersRef.current.length
        ? run('nearest', { point: pt, layers: layersRef.current, radiusM: radius, limit: k }, { label: 'Find nearest', signal: ctrl.signal })
        : Promise.resolve([]),
    ]);
    if (!mounted.current || mine !== seq.current) return;
    if (localRes.status === 'rejected' && isAbortError(localRes.reason)) return;
    const errors = [];
    if (dbRes.status === 'rejected') errors.push('Saved features unavailable');
    if (localRes.status === 'rejected') errors.push('Layer search failed');
    setState({
      db: dbRes.status === 'fulfilled' ? dbRes.value : [],
      local: localRes.status === 'fulfilled' ? localRes.value : [],
      busy: false,
      error: errors.length ? errors.join('; ') : null,
    });
  }, []);

  const search = useCallback((evt) => {
    const pt = [evt.lngLat.lng, evt.lngLat.lat];
    setPoint(pt);
    execute(pt, radiusM, limit);
  }, [execute, radiusM, limit]);

  // Re-run at the same point when the radius / limit change (debounced)
  const pointRef = useRef(null);
  pointRef.current = point;
  useEffect(() => {
    if (!pointRef.current) return undefined;
    const t = setTimeout(() => execute(pointRef.current, radiusM, limit), 300);
    return () => clearTimeout(t);
  }, [radiusM, limit, execute]);

  const clear = useCallback(() => {
    seq.current++;
    if (abortRef.current) abortRef.current.abort();
    setPoint(null);
    setState({ db: [], local: [], busy: false, error: null });
  }, []);

  // Leaving the tool drops the highlight
  useEffect(() => { if (tool !== 'nearest') clear(); }, [tool, clear]);

  const rows = useMemo(() => mergeNearest(state.db, state.local, { radiusM, limit }), [state.db, state.local, radiusM, limit]);
  const geojson = useMemo(() => nearestGeoJSON(point, rows), [point, rows]);

  return { point, rows, geojson, busy: state.busy, error: state.error, radiusM, setRadiusM, limit, setLimit, search, clear };
}
